// Modules
import { useEffect, useMemo } from 'react'

// Hooks
import useNavigateWithParameters from 'hooks/external/useNavigateWithParameters'
import useSingleSignOn from './useSingleSignOn'

// Data
import products from 'data/products'

// Constants
import { FALLBACK_URL, IS_LIVE, IS_ON_PREMISE } from 'constants/environment'

// Data (Local)
const ssoHosts = ['dev-label.aixplain.com', 'localhost:4200', 'api-vision.apptek.com']
const loopbackHostnames = ['localhost', '127.0.0.1', '[::1]'] // Allowed on non-live builds only, so locally served applications can receive the handoff

// Functions (Local)
function getHostname (value) {
	try {
		return (new URL(value)).hostname
	} catch (error) {
		return null
	}
}
function getAllowedHostnames () {
	return [
		...Object.values(products).map(product => getHostname(product?.url)), // Configured products
		getHostname(FALLBACK_URL), // Default redirection target, required by every deployment
		...(!IS_LIVE ? loopbackHostnames : []), // Local development only, never on test, production or on-premise
		...(window.env?.ALLOWED_REDIRECT_HOSTS || '')
			.split(',')
			.map(host => host.trim())
			.map(host => getHostname(host) || host) // Additional hosts set at runtime, accepts both "example.com" and "https://example.com"
	].filter(Boolean)
}

// Function: Redirect > Get redirect token type
// Returns the token type to hand over, or null when the host is not allowed to receive tokens.
// Options exist so the decision can be tested without rebuilding the environment constants.
function getRedirectTokenType (url, options = {}) {
	const {
		allowedHostnames = getAllowedHostnames(),
		isLive = IS_LIVE,
		isOnPremise = IS_ON_PREMISE,
		willWarn = true
	} = options
	const hostname = url?.hostname
	if (!hostname) return null
	if (isLive && !isOnPremise && url?.protocol !== 'https:') return null // Cloud never hands tokens over cleartext
	const product = Object
		.keys(products)
		.find(product => !!products[product]?.url ? getHostname(products[product].url) === hostname : false)
	if (!!product) return products[product]?.tokenType
	if (allowedHostnames.includes(hostname)) return 'platform'
	if (/^(.*\.)?aixplain\.com$/.test(hostname)) return 'platform'
	if (willWarn && !ssoHosts.includes(hostname)) {
		console.warn(`AIX | Redirection to "${hostname}" is blocked because it is not an allowed host. Add it to "ALLOWED_REDIRECT_HOSTS" in the environment configuration if it is expected.`)
	}
	return null
}

// Hook: External > Redirect
function useRedirect ({ willSkip } = {}) {
	// Data
	const { navigateWithParameters } = useNavigateWithParameters()

	// Data (Memoized)
	const redirectURL = useMemo(() => {
		const storedRedirectURL = localStorage.getItem('redirectURL')
		const url = storedRedirectURL || FALLBACK_URL
		if (!url) return null
		try {
			return new URL(decodeURIComponent(url))
		} catch (error) {
			console.warn('AIX | Redirection URL could not be parsed, ignoring it:', url)
			return null
		}
	}, [])
	const tokenType = useMemo(() => getRedirectTokenType(redirectURL), [redirectURL])

	// Data
	const isUsingSSO = ssoHosts.includes(redirectURL?.hostname)
	const isAllowedToRedirect = !!tokenType
	const willRedirect = !willSkip && (isUsingSSO || isAllowedToRedirect)

	// Data
	useSingleSignOn({ redirectURL, willSkip: willSkip || !isUsingSSO }) // TODO: Remove when BE migrates to this app from old SSO

	// Effects
	useEffect(() => {
		if (!willSkip && !isUsingSSO && isAllowedToRedirect) {
			navigateWithParameters(redirectURL.href, tokenType)
		}
	}, [isAllowedToRedirect, isUsingSSO, navigateWithParameters, redirectURL, tokenType, willSkip])

	// Response
	return { willRedirect }
}

// Export
export default useRedirect
export {
	getAllowedHostnames,
	getRedirectTokenType
}
